import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { propertiesAPI, leadsAPI } from '@/lib/api';
import { useSettings } from '@/contexts/SettingsContext';
import { validateName } from '@/lib/validation';
import {
  ArrowLeft, Clock, Users, MapPin, Euro, Edit2, Save, X, Plus, Trash2,
  Sparkles, Briefcase, Tag, Image as ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const HERO_GRADIENTS = [
  'from-emerald-500 to-teal-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-600',
  'from-sky-500 to-blue-600',
];

const labelize = (s) => (s || '').charAt(0).toUpperCase() + (s || '').slice(1).replace(/_/g, ' ');

const INTEREST_LABELS = { low: 'Laag', medium: 'Gemiddeld', high: 'Hoog' };

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [svc, setSvc] = useState(null);
  const [linkedLeads, setLinkedLeads] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showLinkLead, setShowLinkLead] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const { settings, formatCurrency, statusMap } = useSettings();

  const fetchSvc = useCallback(async () => {
    try {
      const res = await propertiesAPI.get(id);
      setSvc(res.data);
      setEditForm(res.data);
    } catch { navigate('/properties'); }
  }, [id, navigate]);

  const fetchLinkedLeads = useCallback(async () => {
    try {
      const res = await propertiesAPI.getLeads(id);
      setLinkedLeads(res.data);
    } catch {}
  }, [id]);

  useEffect(() => { fetchSvc(); fetchLinkedLeads(); }, [fetchSvc, fetchLinkedLeads]);

  const handleSave = async () => {
    const errs = {};
    const titleErr = validateName(editForm.title, 'Naam', { min: 2, max: 200, allowNumbers: true });
    if (titleErr) errs.title = titleErr;
    if (editForm.price != null && editForm.price !== '' && Number(editForm.price) < 0) errs.price = 'Tarief moet positief zijn';
    if (editForm.max_participants && Number(editForm.max_participants) < 0) errs.max_participants = 'Mag niet negatief zijn';

    setEditErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(Object.values(errs)[0]);
      return;
    }

    try {
      await propertiesAPI.update(id, {
        title: editForm.title?.trim(),
        address: editForm.address?.trim() || null,
        type: editForm.type || null,
        status: editForm.status || null,
        price: editForm.price !== '' && editForm.price != null ? parseFloat(editForm.price) : 0,
        duration: editForm.duration?.trim() || null,
        max_participants: editForm.max_participants ? parseInt(editForm.max_participants) : null,
        description: editForm.description ?? null,
        custom_fields: editForm.custom_fields ?? null,
      });
      toast.success('Bijgewerkt');
      setEditing(false);
      setEditErrors({});
      fetchSvc();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg : (typeof detail === 'string' ? detail : 'Bijwerken mislukt'));
    }
  };

  const handleUnlink = async (linkId) => {
    await propertiesAPI.unlinkLead(linkId);
    toast.success('Ontkoppeld');
    fetchLinkedLeads();
    fetchSvc();
  };

  if (!svc) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const cover = svc.photos?.[0];
  const gradient = HERO_GRADIENTS[(svc.id?.charCodeAt(5) || 0) % HERO_GRADIENTS.length];
  const statusInfo = statusMap[svc.status] || { label: svc.status, color: '#6B7280' };

  // Stat cards — only show what's filled
  const stats = [
    svc.price > 0 ? { icon: Euro, label: 'Tarief', value: formatCurrency(svc.price) } : null,
    svc.duration ? { icon: Clock, label: 'Duur', value: svc.duration } : null,
    svc.max_participants ? { icon: Users, label: 'Max deelnemers', value: svc.max_participants } : null,
    svc.address ? { icon: MapPin, label: 'Locatie', value: svc.address } : null,
  ].filter(Boolean);

  return (
    <div className="space-y-6 fade-in" data-testid="property-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/properties')} data-testid="back-to-properties">
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug
        </Button>
      </div>

      {/* Hero */}
      <div className="relative h-56 md:h-72 rounded-lg overflow-hidden">
        {cover ? (
          <img src={cover} alt={svc.title} className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
            <Briefcase className="w-24 h-24 text-white/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-6 left-6 right-6 text-white">
          <div className="flex gap-2 mb-2 flex-wrap">
            <Badge className="text-white border-0" style={{ backgroundColor: statusInfo.color }}>{statusInfo.label}</Badge>
            {svc.type && (
              <Badge variant="secondary" className="bg-white/20 text-white border-0 backdrop-blur-sm">
                <Tag className="w-3 h-3 mr-1" />{labelize(svc.type)}
              </Badge>
            )}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold drop-shadow-md">{svc.title}</h1>
          {svc.address && (
            <p className="text-white/90 flex items-center gap-1 mt-1 text-sm drop-shadow">
              <MapPin className="w-4 h-4" />{svc.address}
            </p>
          )}
        </div>
        <div className="absolute top-4 right-4 flex gap-2">
          {editing ? (
            <>
              <Button size="sm" onClick={handleSave} className="bg-white/90 text-black hover:bg-white">
                <Save className="w-4 h-4 mr-1" />Opslaan
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditForm(svc); setEditErrors({}); }} className="bg-white/90 text-black hover:bg-white">
                <X className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setEditing(true)} className="bg-white/90 text-black hover:bg-white" data-testid="edit-property-btn">
              <Edit2 className="w-4 h-4 mr-1" />Bewerken
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stat cards */}
          {stats.length > 0 && (
            <div className={`grid grid-cols-2 md:grid-cols-${Math.min(stats.length, 4)} gap-3`}>
              {stats.map((stat, i) => (
                <Card key={i} className="border-border/60">
                  <CardContent className="p-4 text-center">
                    <stat.icon className="w-5 h-5 mx-auto mb-1 text-primary" />
                    <p className="text-base font-bold truncate" title={String(stat.value)}>{stat.value}</p>
                    <p className="text-[10px] text-muted-foreground uppercase mt-0.5">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Beschrijving */}
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Beschrijving</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {editing ? (
                <Textarea value={editForm.description || ''} onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))} rows={6} />
              ) : (
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{svc.description || 'Nog geen beschrijving toegevoegd.'}</p>
              )}
              {svc.ai_description && !editing && svc.ai_description !== svc.description && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-2" data-testid="ai-description">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    <span className="text-xs font-semibold text-primary">AI-gegenereerde beschrijving</span>
                  </div>
                  <p className="text-sm leading-relaxed">{svc.ai_description}</p>
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={async () => {
                      await propertiesAPI.update(id, { description: svc.ai_description });
                      toast.success('Beschrijving toegepast');
                      fetchSvc();
                    }}
                    data-testid="apply-ai-description-btn">
                    Gebruik deze beschrijving
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Custom fields display */}
          {!editing && settings.custom_fields_properties && settings.custom_fields_properties.length > 0 && (() => {
            const hasValues = settings.custom_fields_properties.some(field => {
              const v = svc.custom_fields?.[field.key];
              return v !== null && v !== undefined && v !== '';
            });
            if (!hasValues) return null;
            return (
              <Card className="border-border/60">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Aanvullende informatie</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 text-sm">
                  {settings.custom_fields_properties.map((field) => {
                    const value = svc.custom_fields?.[field.key];
                    if (!value && value !== 0 && value !== false) return null;
                    let displayValue = value;
                    if (field.type === 'checkbox') displayValue = value ? 'Ja' : 'Nee';
                    else if (field.type === 'date') displayValue = new Date(value).toLocaleDateString('nl-NL');
                    return (
                      <div key={field.key}>
                        <p className="text-xs text-muted-foreground mb-0.5">{field.label}</p>
                        <p className="font-medium">{displayValue}</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })()}

          {/* Workshop builder selections (read-only) */}
          {!editing && svc.custom_fields?.workshop_builder && svc.custom_fields?.selections && (
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-700" /> Workshop-bouwer keuzes
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-1.5">
                {Object.entries(svc.custom_fields.selections).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}:</span>
                    <span className="font-medium">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Edit form */}
          {editing && (
            <Card className="border-border/60">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Details bewerken</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Naam *</Label>
                  <Input value={editForm.title || ''} onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))} className={editErrors.title ? 'border-destructive' : ''} />
                  {editErrors.title && <p className="text-[10px] text-destructive mt-0.5">{editErrors.title}</p>}
                </div>

                <div>
                  <Label className="text-xs flex items-center gap-1"><Tag className="w-3 h-3" /> Categorie</Label>
                  <select value={editForm.type || ''} onChange={(e) => setEditForm(p => ({ ...p, type: e.target.value }))}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                    <option value="">Selecteer...</option>
                    {(settings.property_types || []).map(t => <option key={t} value={t}>{labelize(t)}</option>)}
                  </select>
                </div>

                <div>
                  <Label className="text-xs">Status</Label>
                  <select value={editForm.status || ''} onChange={(e) => setEditForm(p => ({ ...p, status: e.target.value }))}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                    <option value="">Selecteer...</option>
                    {(settings.property_statuses || []).map(s => {
                      const key = typeof s === 'object' ? s.key : s;
                      const label = typeof s === 'object' ? s.label : labelize(s);
                      return <option key={key} value={key}>{label}</option>;
                    })}
                  </select>
                </div>

                <div className="col-span-2">
                  <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Locatie</Label>
                  <Input value={editForm.address || ''} onChange={(e) => setEditForm(p => ({ ...p, address: e.target.value }))} placeholder="optioneel" />
                </div>

                <div>
                  <Label className="text-xs flex items-center gap-1"><Euro className="w-3 h-3" /> Tarief</Label>
                  <Input type="number" min="0" step="0.01" value={editForm.price || ''}
                    onChange={(e) => setEditForm(p => ({ ...p, price: e.target.value }))}
                    className={editErrors.price ? 'border-destructive' : ''} />
                </div>

                <div>
                  <Label className="text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> Duur</Label>
                  <Input value={editForm.duration || ''} onChange={(e) => setEditForm(p => ({ ...p, duration: e.target.value }))} placeholder="bijv. 90 min" />
                </div>

                <div>
                  <Label className="text-xs flex items-center gap-1"><Users className="w-3 h-3" /> Max deelnemers</Label>
                  <Input type="number" min="0" max="10000" value={editForm.max_participants || ''}
                    onChange={(e) => setEditForm(p => ({ ...p, max_participants: e.target.value }))} />
                </div>

                {/* Custom fields edit */}
                {settings.custom_fields_properties && settings.custom_fields_properties.length > 0 && (
                  <>
                    <div className="col-span-2 border-t pt-2 mt-1">
                      <p className="text-xs font-semibold text-muted-foreground">Extra velden</p>
                    </div>
                    {settings.custom_fields_properties.map((field) => (
                      <div key={field.key} className={field.type === 'textarea' ? 'col-span-2' : ''}>
                        <Label className="text-xs">{field.label} {field.required && <span className="text-destructive">*</span>}</Label>
                        <CustomFieldEditInput field={field}
                          value={editForm.custom_fields?.[field.key]}
                          onChange={(val) => setEditForm(p => ({ ...p, custom_fields: { ...(p.custom_fields || {}), [field.key]: val } }))} />
                      </div>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Side column */}
        <div className="space-y-4">
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Gekoppelde leads</CardTitle>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setShowLinkLead(true)} data-testid="link-lead-btn">
                  <Plus className="w-3 h-3" /> Koppelen
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {linkedLeads.map((link) => (
                <div key={link.id} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                  <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(`/leads/${link.lead_id}`)}>
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                      {link.lead_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{link.lead_name}</p>
                      <Badge variant="secondary" className="text-[8px]">
                        {INTEREST_LABELS[link.interest_level] || link.interest_level}
                      </Badge>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => handleUnlink(link.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {linkedLeads.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">Nog geen leads gekoppeld</p>
              )}
            </CardContent>
          </Card>

          {/* Extra afbeeldingen */}
          {svc.photos?.length > 1 && (
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5" /> Afbeeldingen
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-2">
                {svc.photos.map((photo, i) => (
                  <img key={i} src={photo} alt={`Afbeelding ${i + 1}`} className="rounded-md w-full h-24 object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <LinkLeadDialog open={showLinkLead} onClose={() => setShowLinkLead(false)} propertyId={id}
        onSuccess={() => { fetchLinkedLeads(); fetchSvc(); }} />
    </div>
  );
}

function CustomFieldEditInput({ field, value, onChange }) {
  switch (field.type) {
    case 'text':
      return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} />;
    case 'textarea':
      return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2} />;
    case 'number':
      return <Input type="number" value={value || ''} onChange={(e) => onChange(parseFloat(e.target.value) || '')} />;
    case 'select':
      return (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm">
          <option value="">Selecteer...</option>
          {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    case 'checkbox':
      return (
        <div className="flex items-center gap-2 h-9">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
        </div>
      );
    case 'date':
      return <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
    default:
      return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} />;
  }
}

function LinkLeadDialog({ open, onClose, propertyId, onSuccess }) {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState('');
  const [interest, setInterest] = useState('medium');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      leadsAPI.list({ limit: 100 }).then(res => setLeads(res.data.leads)).catch(() => {});
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;
    setSaving(true);
    try {
      await propertiesAPI.linkLead({ lead_id: selectedLead, property_id: propertyId, interest_level: interest });
      toast.success('Lead gekoppeld');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mislukt');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="link-lead-dialog">
        <DialogHeader><DialogTitle>Lead koppelen aan dienst</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Selecteer lead</Label>
            <select value={selectedLead || ''} onChange={(e) => setSelectedLead(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
              <option value="">Kies een lead...</option>
              {leads.map(l => <option key={l.id} value={l.id}>{l.name} ({l.email || l.phone})</option>)}
            </select>
          </div>
          <div>
            <Label>Interesseniveau</Label>
            <select value={interest} onChange={(e) => setInterest(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
              <option value="low">Laag</option>
              <option value="medium">Gemiddeld</option>
              <option value="high">Hoog</option>
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Annuleren</Button>
            <Button type="submit" disabled={saving || !selectedLead} data-testid="save-link-btn">
              {saving ? 'Koppelen...' : 'Koppelen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
